import React, { useEffect, useState } from 'react';
import { HiPlay, HiPencil, HiTrash } from 'react-icons/hi';
import { syncSourcesService } from '../services/api';
import SyncSourceFormModal from '../components/SyncSourceFormModal';
import { apiError } from '../utils/apiError';

/**
 * Admin page for sync sources — the S3 folders the poller watches.
 *
 * Each row used to be a hardcoded entry in _SOURCE_CONFIGS plus a prefix field
 * in Settings; adding a folder now means adding a row here, with no code change
 * and no redeploy.
 */
const SyncSourcesPage = () => {
  const [sources, setSources] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null); // source object, or 'new'
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null); // row-level action in flight

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    Promise.all([syncSourcesService.list(), syncSourcesService.getOptions()])
      .then(([list, opts]) => {
        setSources(list);
        setOptions(opts);
      })
      .catch((err) => showToast('error', apiError(err, 'Failed to load sync sources')))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (formValues) => {
    setSaving(true);
    try {
      const saved =
        editing === 'new'
          ? await syncSourcesService.create(formValues)
          : await syncSourcesService.update(editing.id, formValues);

      if (formValues.is_upload_target) {
        // The server moves this flag off whatever source held it for the same
        // site + document type, so a local patch would leave two rows badged.
        setSources(await syncSourcesService.list());
      } else if (editing === 'new') {
        setSources((prev) => [...prev, saved]);
      } else {
        setSources((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
      }

      showToast('success', `Sync source "${saved.name}" ${editing === 'new' ? 'created' : 'updated'}`);
      setEditing(null);
    } catch (err) {
      showToast('error', apiError(err, 'Failed to save sync source'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (source) => {
    const ok = window.confirm(
      `Delete sync source "${source.name}"?\n\nThe folder stops being polled. Documents already synced from it stay searchable.`
    );
    if (!ok) return;

    setBusyId(source.id);
    try {
      await syncSourcesService.remove(source.id);
      setSources((prev) => prev.filter((s) => s.id !== source.id));
      showToast('success', `Sync source "${source.name}" deleted`);
    } catch (err) {
      showToast('error', apiError(err, 'Failed to delete sync source'));
    } finally {
      setBusyId(null);
    }
  };

  const handleTrigger = async (source) => {
    setBusyId(source.id);
    try {
      const res = await syncSourcesService.trigger(source.id);
      showToast('success', res.message || `Sync triggered for "${source.name}"`);
    } catch (err) {
      showToast('error', apiError(err, 'Failed to trigger sync'));
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleEnabled = async (source) => {
    setBusyId(source.id);
    try {
      const updated = await syncSourcesService.update(source.id, { enabled: !source.enabled });
      setSources((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (err) {
      showToast('error', apiError(err, 'Failed to update sync source'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="page-container"><p className="text-slate-500">Loading…</p></div>;
  }

  return (
    <div className="page-container max-w-6xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-1">Sync Sources</h2>
          <p className="text-slate-500">
            S3 folders the poller watches. Add a folder here instead of changing code —
            it is picked up on the next scheduled run.
          </p>
        </div>
        <button
          onClick={() => setEditing('new')}
          className="shrink-0 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Add Source
        </button>
      </div>

      {toast && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left py-3 px-4 font-semibold text-slate-600">Name</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-600">Site</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-600">Type</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-600">Dept</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-600">S3 Prefix</th>
              <th className="text-center py-3 px-3 font-semibold text-slate-600">Enabled</th>
              <th className="py-3 px-4 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => {
              const isBusy = busyId === s.id;
              return (
                <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{s.name}</span>
                      {s.is_upload_target && (
                        <span
                          title="Manual uploads for this site and document type land in this folder"
                          className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-indigo-50 text-indigo-700"
                        >
                          upload
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 font-mono">{s.key}</div>
                  </td>
                  <td className="py-3 px-3 text-slate-600">{s.site_name || s.site_slug}</td>
                  <td className="py-3 px-3">
                    <div className="text-slate-700">{s.document_type}</div>
                    <div className="text-xs text-slate-400">{s.source_type}</div>
                  </td>
                  <td className="py-3 px-3 text-slate-600">{s.department || '—'}</td>
                  <td className="py-3 px-3">
                    <div className="font-mono text-xs text-slate-700 break-all">{s.s3_prefix}</div>
                    {s.exclude_prefix && (
                      <div className="font-mono text-xs text-amber-600 break-all">
                        excl: {s.exclude_prefix}
                      </div>
                    )}
                    {s.start_delay_seconds > 0 && (
                      <div className="text-xs text-slate-400">delay {s.start_delay_seconds}s</div>
                    )}
                  </td>
                  <td className="py-3 px-3 text-center">
                    <input
                      type="checkbox"
                      checked={s.enabled}
                      disabled={isBusy}
                      onChange={() => handleToggleEnabled(s)}
                      className="w-4 h-4 accent-blue-600 cursor-pointer disabled:opacity-50"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleTrigger(s)}
                        disabled={isBusy || !s.enabled}
                        title={s.enabled ? 'Run this source now' : 'Enable the source to run it'}
                        className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <HiPlay className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditing(s)}
                        disabled={isBusy}
                        title="Edit"
                        className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30"
                      >
                        <HiPencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        disabled={isBusy}
                        title="Delete"
                        className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                      >
                        <HiTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {sources.length === 0 && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-slate-400">
                  No sync sources yet. Add one to start polling an S3 folder.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Disabled sources are skipped by both the scheduled poll and the daily reconciliation.
        Deleting a source stops polling but keeps the documents already synced from it.
        An <span className="font-semibold text-indigo-600">upload</span> badge marks the folder
        that receives manual uploads for that site and document type.
      </p>

      {editing && options && (
        <SyncSourceFormModal
          source={editing === 'new' ? null : editing}
          options={options}
          saving={saving}
          onSubmit={handleSubmit}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default SyncSourcesPage;
